/**
 * NO04 - Ordre List Component
 */

import { useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    Box,
    CircularProgress,
    Checkbox,
    Chip,
    Button,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    IconButton,
} from '@mui/material';
import { Download as DownloadIcon, List as ListIcon, Close as CloseIcon } from '@mui/icons-material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import NO04 from '../../../models/NO04';
import * as actionsNO04 from '../../../store/actions/indexNO04';
import CSharpUtils from '../../../felles/CSharpUtils';

// Custom status colors matching the design (chip border + text color)
const STATUS_STYLE: Record<string, { border: string; color: string; bg: string }> = {
    'Registrert':    { border: '#1976d2', color: '#1976d2', bg: '#e3f0fc' },
    'Innmeldt':      { border: '#1976d2', color: '#1976d2', bg: '#e3f0fc' },
    'Printet':       { border: '#7b1fa2', color: '#7b1fa2', bg: '#f3e5f5' },
    'Pakket':        { border: '#7b1fa2', color: '#7b1fa2', bg: '#f3e5f5' },
    'Ferdigmeldt':   { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Lageroverf.':   { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Ikke fullført': { border: '#d32f2f', color: '#d32f2f', bg: '#fdecea' },
    'Kreditstopp':   { border: '#d32f2f', color: '#d32f2f', bg: '#fdecea' },
    'Venteordre':    { border: '#e65100', color: '#e65100', bg: '#fff3e0' },
    'Utgårmeldt':    { border: '#757575', color: '#757575', bg: '#f5f5f5' },
    'Korrigert':     { border: '#757575', color: '#757575', bg: '#f5f5f5' },
    'Fakturert':     { border: '#00796b', color: '#00796b', bg: '#e0f2f1' },
    'Overført':      { border: '#757575', color: '#757575', bg: '#f5f5f5' },
    'Intern':        { border: '#757575', color: '#757575', bg: '#f5f5f5' },
    'Retur lev.':    { border: '#757575', color: '#757575', bg: '#f5f5f5' },
    'Ufullstendig':  { border: '#757575', color: '#757575', bg: '#f5f5f5' },
};

const getStatusStyle = (status: string) =>
    STATUS_STYLE[status] ?? { border: '#9e9e9e', color: '#9e9e9e', bg: '#fafafa' };

const useStyles = makeStyles()((theme) => ({
    container: {
        margin: 0,
        borderRadius: 0,
        boxShadow: 'none',
        borderTop: '1px solid #e0e0e0',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
    },
    toolbarSection: {
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #e0e0e0',
    },
    emptyState: {
        padding: theme.spacing(8),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    loadingContainer: {
        padding: theme.spacing(8),
        display: 'flex',
        justifyContent: 'center',
    },
    tableRow: {
        cursor: 'pointer',
        borderBottom: '1px solid #e8e8e8',
        '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    tableRowRetur: {
        backgroundColor: '#fff6f6',
        cursor: 'pointer',
        borderBottom: '1px solid #e8e8e8',
        '&:hover': { backgroundColor: '#ffe8e8' },
    },
    cell: {
        fontSize: '0.82rem',
        padding: '8px 10px',
        whiteSpace: 'nowrap',
        border: 'none',
    },
    cellTop: {
        fontSize: '0.82rem',
        padding: '8px 10px 2px 10px',
        whiteSpace: 'nowrap',
        border: 'none',
        verticalAlign: 'bottom',
    },
    ordreLink: {
        color: theme.palette.primary.main,
        fontWeight: 700,
        fontSize: '0.88rem',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
    },
    dateTime: {
        color: theme.palette.text.secondary,
        fontSize: '0.78rem',
        marginLeft: 6,
    },
    label: {
        color: theme.palette.text.secondary,
        fontSize: '0.78rem',
        marginRight: 2,
    },
    value: {
        fontSize: '0.82rem',
        fontWeight: 500,
    },
    fraktLine: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        padding: '0px 10px 6px 10px',
        border: 'none',
    },
    negative: { color: theme.palette.error.main, fontWeight: 600 },
    positive: { color: theme.palette.text.primary, fontWeight: 500 },
    pct: {
        fontSize: '0.78rem',
        fontWeight: 600,
        minWidth: 52,
        textAlign: 'right',
    },
    loadMoreContainer: {
        padding: theme.spacing(2),
        display: 'flex',
        justifyContent: 'center',
    },
    selectAllRow: {
        padding: theme.spacing(0.5, 2),
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
    },
}));

export default function NO04List() {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const isFetchingRef = useRef(false);

    const orders = useSelector((state: AppState) => state.NO04Ordre.orders);
    const loading = useSelector((state: AppState) => state.NO04Ordre.loading);
    const loadingMore = useSelector((state: AppState) => state.NO04Ordre.loadingMore);
    const hasMore = useSelector((state: AppState) => state.NO04Ordre.hasMore);
    const currentPage = useSelector((state: AppState) => state.NO04Ordre.currentPage);
    const lastSearchParams = useSelector((state: AppState) => state.NO04Ordre.lastSearchParams);
    const error = useSelector((state: AppState) => state.NO04Ordre.error);
    const orderDetail = useSelector((state: AppState) => state.NO04Ordre.orderDetail);
    const orderDetailLoading = useSelector((state: AppState) => state.NO04Ordre.orderDetailLoading);

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [dialogOpen, setDialogOpen] = useState(false);

    const handleOrdreNrClick = (ordreNr: string) => {
        dispatch(actionsNO04.NO04OrderDetailRequest(CSharpUtils.T_companyID, ordreNr));
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        dispatch(actionsNO04.NO04OrderDetailClear());
    };

    const formatDateTime = (dateStr: string, tid: string) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const date = d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: '2-digit' });
        if (tid) {
            const t = tid.toString().padStart(6, '0');
            const time = `${t.substring(0, 2)}:${t.substring(2, 4)}`;
            return `${date} ${time}`;
        }
        return date;
    };

    const formatAmount = (amount: number) =>
        new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

    const formatPct = (pct: number) => {
        if (pct === 0 || pct == null) return null;
        const formatted = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(pct);
        return pct < 0 ? `${formatted}%` : `${formatted}%`;
    };

    const isRetur = (order: NO04) => order.ordreNr.startsWith('9');

    const allSelected = selected.size === orders.length && orders.length > 0;

    const handleSelectAll = (checked: boolean) => {
        setSelected(checked ? new Set(orders.map((o) => o.ordreNr)) : new Set());
    };

    const handleSelectRow = (ordreNr: string, checked: boolean) => {
        const next = new Set(selected);
        checked ? next.add(ordreNr) : next.delete(ordreNr);
        setSelected(next);
    };

    const [exportingLines, setExportingLines] = useState(false);

    const triggerDownload = (content: string, filename: string) => {
        const bom = '\uFEFF';
        const el = document.createElement('a');
        el.setAttribute('href', `data:text/csv;charset=utf-8,${encodeURIComponent(bom + content)}`);
        el.setAttribute('download', filename);
        document.body.appendChild(el);
        el.click();
        document.body.removeChild(el);
    };

    // Excel — exports the current order list
    const exportOrdersToCSV = () => {
        const toExport = selected.size > 0
            ? orders.filter((o) => selected.has(o.ordreNr))
            : orders;
        if (toExport.length === 0) return;

        const headers = ['OrdreNr','Dato','Tid','Kundenr','Navn','RekvNr','OM','Status','Bestilt','Levert','Beløp','BRF%','Faktura','Sporing','SendeMåte'];
        const rows = toExport.map((o) => [
            o.ordreNr,
            o.dato ? new Date(o.dato).toLocaleDateString('nb-NO') : '',
            o.tid ? `${o.tid.toString().padStart(6,'0').substring(0,2)}:${o.tid.toString().padStart(6,'0').substring(2,4)}` : '',
            o.kundenr,
            o.navn,
            o.rekvNr,
            o.om,
            o.status,
            o.antBestilt,
            o.antLevert,
            o.belop,
            o.brfProsent,
            o.faktura,
            o.fraktEdi,
            o.sendeMote,
        ].join(';'));

        triggerDownload([headers.join(';'), ...rows].join('\n'), `Ordre_${Date.now()}.csv`);
    };

    // Med ordrelinjer — fetches detail per order and exports flattened lines
    const exportOrdersWithLines = async () => {
        const toExport = selected.size > 0
            ? orders.filter((o) => selected.has(o.ordreNr))
            : orders;
        if (toExport.length === 0) return;

        setExportingLines(true);
        try {
            const { fetchOrderDetail } = await import('../../../felles/APICalls_NO04');
            const companyId = Number(CSharpUtils.T_companyID);

            const headers = ['OrdreNr','Dato','Kundenr','Navn','RekvNr','Status','Alfa','Artikkel','Bestilt','Levert','Tekst','VPris','Rabatt','NPris'];
            const rows: string[] = [];

            for (const order of toExport) {
                try {
                    const res = await fetchOrderDetail(companyId, order.ordreNr);
                    const detail = res.data;
                    const dato = order.dato ? new Date(order.dato).toLocaleDateString('nb-NO') : '';
                    const varer: any[] = detail?.varer ?? [];
                    if (varer.length === 0) {
                        rows.push([order.ordreNr, dato, order.kundenr, order.navn, order.rekvNr, order.status, '', '', '', '', '', '', '', ''].join(';'));
                    } else {
                        varer.forEach((v: any) => {
                            rows.push([
                                order.ordreNr, dato, order.kundenr, order.navn, order.rekvNr, order.status,
                                v.alfa ?? '', v.artikkel ?? '', v.bestilt ?? '', v.levert ?? '',
                                v.tekst ?? '', v.vpris ?? '', v.rab1 ?? '', v.npris ?? '',
                            ].join(';'));
                        });
                    }
                } catch {
                    rows.push([order.ordreNr, '', order.kundenr, order.navn, '', order.status, 'FEIL', '', '', '', '', '', '', ''].join(';'));
                }
            }

            triggerDownload([headers.join(';'), ...rows].join('\n'), `Ordre_MedLinjer_${Date.now()}.csv`);
        } finally {
            setExportingLines(false);
        }
    };

    // Infinite scroll
    useEffect(() => {
        isFetchingRef.current = loadingMore;
    }, [loadingMore]);

    useEffect(() => {
        const handleScroll = () => {
            const container = tableContainerRef.current;
            if (!container || isFetchingRef.current || !hasMore || !lastSearchParams) return;
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollHeight - scrollTop - clientHeight < 150) {
                isFetchingRef.current = true;
                dispatch(actionsNO04.NO04LoadMoreRequest({
                    ...lastSearchParams,
                    page: currentPage + 1,
                    pageSize: 100,
                }));
            }
        };
        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [hasMore, currentPage, lastSearchParams, loading, dispatch]);

    if (loading) {
        return (
            <Paper className={classes.container}>
                <Box className={classes.loadingContainer}><CircularProgress /></Box>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper className={classes.container}>
                <Box className={classes.emptyState}>
                    <Typography color="error">{error}</Typography>
                </Box>
            </Paper>
        );
    }

    if (orders.length === 0) {
        return (
            <Paper className={classes.container}>
                <Box className={classes.emptyState}>
                    <Typography variant="h6">Ingen ordre funnet</Typography>
                    <Typography variant="body2">Søk på ordre siste 5 år</Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper className={classes.container}>
            {/* Toolbar: Excel + Med ordrelinjer */}
            <Box className={classes.toolbarSection}>
                <Box />
                <Box display="flex" gap={1}>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportOrdersToCSV}>
                        Excel
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={exportingLines ? <CircularProgress size={14} /> : <ListIcon />}
                        onClick={exportOrdersWithLines}
                        disabled={exportingLines}
                    >
                        Med ordrelinjer
                    </Button>
                </Box>
            </Box>

            {/* Select all row */}
            <Box className={classes.selectAllRow}>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSelectAll(!allSelected)}
                    sx={{ fontSize: '0.8rem', textTransform: 'none', minWidth: 'unset', borderColor: '#d30535', color: '#d30535', '&:hover': { borderColor: '#b0022c', color: '#b0022c', backgroundColor: 'rgba(211,5,53,0.04)' } }}
                >
                    Velg alle
                </Button>
            </Box>

            {/* List */}
            <TableContainer ref={tableContainerRef} sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <Table size="small" sx={{ tableLayout: 'auto' }}>
                    <TableBody>
                        {orders.map((order: NO04, index: number) => {
                            const pct = formatPct(order.brfProsent);
                            const hasFrakt = !!order.fraktEdi;
                            return (
                                <TableRow
                                    key={`${order.ordreNr}-${index}`}
                                    className={isRetur(order) ? classes.tableRowRetur : classes.tableRow}
                                    sx={{ '& td': { verticalAlign: 'middle' } }}
                                >
                                    {/* Checkbox */}
                                    <TableCell padding="checkbox" className={classes.cell} sx={{ pl: 1 }}>
                                        <Checkbox
                                            size="small"
                                            checked={selected.has(order.ordreNr)}
                                            onChange={(e) => handleSelectRow(order.ordreNr, e.target.checked)}
                                        />
                                    </TableCell>

                                    {/* OrdreNr + date/time */}
                                    <TableCell className={classes.cell} sx={{ minWidth: 160 }}>
                                        <Box display="flex" alignItems="baseline" gap={0.5}>
                                            <Link
                                                className={classes.ordreLink}
                                                underline="hover"
                                                onClick={() => handleOrdreNrClick(order.ordreNr)}
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {order.ordreNr}
                                            </Link>
                                            <span className={classes.dateTime}>
                                                {formatDateTime(order.dato, order.tid)}
                                            </span>
                                        </Box>
                                        {order.faktura && (
                                            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.3 }}>
                                                <span style={{ marginRight: 2 }}>Fakt:</span>
                                                {order.faktura}
                                            </Box>
                                        )}
                                    </TableCell>

                                    {/* Kundenr */}
                                    <TableCell className={classes.cell} sx={{ minWidth: 100 }}>
                                        <span className={classes.label}>Kunde:</span>
                                        <span className={classes.value}>{order.kundenr}</span>
                                    </TableCell>

                                    {/* Navn + Frakt/Sporing */}
                                    <TableCell className={classes.cell} sx={{ minWidth: 160 }}>
                                        <Box>
                                            <Box sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                                                {order.navn}
                                            </Box>
                                            {hasFrakt && (
                                                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.3 }}>
                                                    {order.sendeMote && (
                                                        <span style={{ marginRight: 4 }}>{order.sendeMote} –</span>
                                                    )}
                                                    Frakt/Sporing:{' '}
                                                    <Link href="#" underline="hover" sx={{ fontSize: '0.75rem' }}>
                                                        {order.fraktEdi}
                                                    </Link>
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>

                                    {/* Rekv.nr */}
                                    <TableCell className={classes.cell}>
                                        <span className={classes.label}>Rekv:</span>
                                        <span className={classes.value}>{order.rekvNr}</span>
                                    </TableCell>

                                    {/* Status chip */}
                                    <TableCell className={classes.cell}>
                                        {(() => {
                                            const s = getStatusStyle(order.status);
                                            return (
                                                <Chip
                                                    label={order.status}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: '0.72rem',
                                                        height: 22,
                                                        borderColor: s.border,
                                                        color: s.color,
                                                        backgroundColor: s.bg,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            );
                                        })()}
                                    </TableCell>

                                    {/* OM */}
                                    <TableCell className={classes.cell}>
                                        <span className={classes.label}>OM:</span>
                                        <span className={classes.value}>{order.om}</span>
                                    </TableCell>

                                    {/* Best + Lev */}
                                    <TableCell className={classes.cell}>
                                        <span className={classes.label}>Best:</span>
                                        <span className={classes.value}>{order.antBestilt}</span>
                                        {'  '}
                                        <span className={classes.label}>Lev:</span>
                                        <span
                                            className={classes.value}
                                            style={{ color: order.antLevert > 0 ? '#1976d2' : undefined }}
                                        >
                                            {order.antLevert}
                                        </span>
                                    </TableCell>

                                    {/* Beløp */}
                                    <TableCell
                                        className={classes.cell}
                                        align="right"
                                        sx={{ minWidth: 80 }}
                                    >
                                        <span className={order.belop < 0 ? classes.negative : classes.positive}>
                                            {formatAmount(order.belop)} kr
                                        </span>
                                    </TableCell>

                                    {/* % */}
                                    <TableCell className={classes.cell} align="right" sx={{ minWidth: 60 }}>
                                        {pct && (
                                            <span
                                                className={classes.pct}
                                                style={{ color: order.brfProsent < 0 ? '#d32f2f' : undefined }}
                                            >
                                                {pct}
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {loadingMore && (
                <Box className={classes.loadMoreContainer}><CircularProgress size={24} /></Box>
            )}

            {/* Order Detail Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                fullWidth
                maxWidth={orderDetail?.varer?.length > 3 ? 'md' : 'sm'}
                sx={{
                    '& .MuiDialog-paper': {
                        minWidth: orderDetail?.varer?.length > 0 ? '600px' : '300px',
                        width: 'auto',
                        maxWidth: 'md',
                    },
                }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Ordredetaljer</Typography>
                        <IconButton onClick={handleDialogClose}><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {orderDetailLoading ? (
                        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                    ) : orderDetail ? (
                        <Box>
                            <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#f0f4fa', mb: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Ordre:</strong> {orderDetail.orderNumber}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Status:</strong> {orderDetail.status}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Sum U/MVA:</strong> {orderDetail.total_pris}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Knr:</strong> {orderDetail.kunde}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Navn:</strong> {orderDetail.navn1}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Ordredato:</strong> {orderDetail.dato}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Adresse:</strong> {orderDetail.adr}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Post/Sted:</strong> {orderDetail.postnr}-{orderDetail.poststed}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Bet.kode:</strong> {orderDetail.BETMET}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>V.adr1:</strong> {orderDetail.vnavn1}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>V.adr2:</strong> {orderDetail.vnavn2}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>V.adr3:</strong> {orderDetail.vnavn3}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>V.adr4:</strong> {orderDetail.vnavn4}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Avd-LK:</strong> {orderDetail.avd}-{orderDetail.lager}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Kred.type:</strong> {orderDetail.kredtype}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Rekv.nr:</strong> {orderDetail.rekvnr}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>OM:</strong> {orderDetail.orderRecipient}</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <Typography variant="body2"><strong>Fors.måte:</strong> {orderDetail.sendeMote}</Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                            {orderDetail.varer?.length > 0 && (
                                <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                                    <Table stickyHeader size="small">
                                        <TableBody>
                                            <TableRow>
                                                {['Alfa','Artikkel','Bestilt','Levert','Tekst','Vpris','Rabatt','Npris','PT','PT-Type'].map((h) => (
                                                    <TableCell key={h} sx={{ fontWeight: 'bold', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                                        {h === 'Vpris' ? (
                                                            <Tooltip title="Pris pr. stk" arrow>
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{h}<InfoOutlined fontSize="small" /></span>
                                                            </Tooltip>
                                                        ) : h === 'Npris' ? (
                                                            <Tooltip title="Ordrepris x antall" arrow>
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{h}<InfoOutlined fontSize="small" /></span>
                                                            </Tooltip>
                                                        ) : h}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                            {orderDetail.varer.map((item: any, idx: number) => (
                                                <TableRow key={idx}>
                                                    <TableCell sx={{ textAlign: 'center' }}>{item.alfa}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{item.artikkel}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{item.bestilt}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{item.levert}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{item.tekst}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{item.vpris}</TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        {item.rab1}{item.rab2 ? ` | ${item.rab2}${item.rab3 || ''}` : ''}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>{item.npris}</TableCell>
                                                    <TableCell>{item.tillegg}</TableCell>
                                                    <TableCell>{item.pris_type || 'N/A'}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    ) : (
                        <Typography>Ingen data tilgjengelig for denne ordren.</Typography>
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
}

